import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useListServicesQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useDeleteServiceMutation,
} from '../../services/rtkApi/adminApi';
import { apiClient } from '../../api/client';
import { useAlert, useConfirm } from '../../ui/components/ConfirmDialog';

interface ServicePermissions {
  sendNotifications: boolean;
  sendActions: boolean;
  sendMedia: boolean;
  broadcastAllowed: boolean;
  readProfile: boolean;
  readStats: boolean;
  readConnections: boolean;
  onUserConnect: boolean;
  onUserDisconnect: boolean;
  onUserAction: boolean;
}

interface Service {
  clientId: string;
  name: string;
  description?: string;
  logoUrl?: string;
  clientType: string;
  redirectUris: string[];
  allowedScopes: string[];
  grantTypes: string[];
  requirePkce: boolean;
  active: boolean;
  trusted: boolean;
  permissions: ServicePermissions;
  webhookUrl?: string;
  accessTokenLifetime: number;
  refreshTokenLifetime: number;
  createdAt: string;
}

const ServicesAdmin: React.FC = () => {
  const alert = useAlert();
  const confirm = useConfirm();
  const { data: services = [], isLoading, error, refetch } = useListServicesQuery();
  const [createService] = useCreateServiceMutation();
  const [updateService] = useUpdateServiceMutation();
  const [deleteService] = useDeleteServiceMutation();

  const [editingService, setEditingService] = useState<Service | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newClientSecret, setNewClientSecret] = useState<string | null>(null);
  const [testNotificationLoading, setTestNotificationLoading] = useState(false);
  const [testNotificationResponse, setTestNotificationResponse] = useState<any>(null);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailResponse, setTestEmailResponse] = useState<any>(null);
  const [testLoggerLoading, setTestLoggerLoading] = useState(false);
  const [testLoggerResponse, setTestLoggerResponse] = useState<any>(null);

  const defaultPermissions: ServicePermissions = {
    sendNotifications: false,
    sendActions: false,
    sendMedia: false,
    broadcastAllowed: false,
    readProfile: false,
    readStats: false,
    readConnections: false,
    onUserConnect: false,
    onUserDisconnect: false,
    onUserAction: false,
  };

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name') as string,
      description: formData.get('description') as string,
      logoUrl: formData.get('logoUrl') as string,
      clientType: formData.get('clientType') as string,
      redirectUris: (formData.get('redirectUris') as string).split(',').map((s) => s.trim()),
      allowedScopes: (formData.get('allowedScopes') as string).split(',').map((s) => s.trim()),
      grantTypes: (formData.get('grantTypes') as string).split(',').map((s) => s.trim()),
      requirePkce: formData.get('requirePkce') === 'on',
      permissions: {
        sendNotifications: formData.get('perm.sendNotifications') === 'on',
        sendActions: formData.get('perm.sendActions') === 'on',
        sendMedia: formData.get('perm.sendMedia') === 'on',
        broadcastAllowed: formData.get('perm.broadcastAllowed') === 'on',
        readProfile: formData.get('perm.readProfile') === 'on',
        readStats: formData.get('perm.readStats') === 'on',
        readConnections: formData.get('perm.readConnections') === 'on',
        onUserConnect: formData.get('perm.onUserConnect') === 'on',
        onUserDisconnect: formData.get('perm.onUserDisconnect') === 'on',
        onUserAction: formData.get('perm.onUserAction') === 'on',
      },
      webhookUrl: formData.get('webhookUrl') as string,
      trusted: formData.get('trusted') === 'on',
      active: formData.get('active') !== null,
      accessTokenLifetime: parseInt(formData.get('accessTokenLifetime') as string) || 3600,
      refreshTokenLifetime: parseInt(formData.get('refreshTokenLifetime') as string) || 604800,
    };

    try {
      const result = await createService(data).unwrap();
      if (result.clientSecret) {
        setNewClientSecret(result.clientSecret);
        setTimeout(() => setNewClientSecret(null), 30000); // hide after 30 seconds
      }
      setShowCreateForm(false);
      refetch();
    } catch (error) {
      console.error('Failed to create service:', error);
      await alert({ title: 'Create failed', message: 'Failed to create service' });
    }
  };

  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingService) return;

    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name') as string,
      description: formData.get('description') as string,
      logoUrl: formData.get('logoUrl') as string,
      redirectUris: (formData.get('redirectUris') as string).split(',').map((s) => s.trim()),
      allowedScopes: (formData.get('allowedScopes') as string).split(',').map((s) => s.trim()),
      grantTypes: (formData.get('grantTypes') as string).split(',').map((s) => s.trim()),
      requirePkce: formData.get('requirePkce') === 'on',
      permissions: {
        sendNotifications: formData.get('perm.sendNotifications') === 'on',
        sendActions: formData.get('perm.sendActions') === 'on',
        sendMedia: formData.get('perm.sendMedia') === 'on',
        broadcastAllowed: formData.get('perm.broadcastAllowed') === 'on',
        readProfile: formData.get('perm.readProfile') === 'on',
        readStats: formData.get('perm.readStats') === 'on',
        readConnections: formData.get('perm.readConnections') === 'on',
        onUserConnect: formData.get('perm.onUserConnect') === 'on',
        onUserDisconnect: formData.get('perm.onUserDisconnect') === 'on',
        onUserAction: formData.get('perm.onUserAction') === 'on',
      },
      webhookUrl: formData.get('webhookUrl') as string,
      trusted: formData.get('trusted') === 'on',
      active: formData.get('active') === 'on',
      accessTokenLifetime: parseInt(formData.get('accessTokenLifetime') as string) || 3600,
      refreshTokenLifetime: parseInt(formData.get('refreshTokenLifetime') as string) || 604800,
    };

    try {
      await updateService({ clientId: editingService.clientId, data }).unwrap();
      setEditingService(null);
      refetch();
    } catch (error) {
      console.error('Failed to update service:', error);
      await alert({ title: 'Update failed', message: 'Failed to update service' });
    }
  };

  const handleDelete = async (clientId: string) => {
    const ok = await confirm({
      title: 'Delete service?',
      message: 'This will permanently remove the service.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await deleteService(clientId).unwrap();
      refetch();
    } catch (error) {
      console.error('Failed to delete service:', error);
      await alert({ title: 'Delete failed', message: 'Failed to delete service' });
    }
  };

  const handleTestNotification = async () => {
    setTestNotificationLoading(true);
    setTestNotificationResponse(null);
    try {
      const response = await apiClient.post('/api/admin/test-notification');
      setTestNotificationResponse({
        success: true,
        data: response.data,
        timestamp: new Date().toISOString(),
      });
    } catch (error: any) {
      setTestNotificationResponse({
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to send test notification',
        timestamp: new Date().toISOString(),
      });
    } finally {
      setTestNotificationLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestEmailLoading(true);
    setTestEmailResponse(null);
    try {
      const response = await apiClient.post('/api/admin/test-email');
      setTestEmailResponse({
        success: true,
        data: response.data,
        timestamp: new Date().toISOString(),
      });
    } catch (error: any) {
      setTestEmailResponse({
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to send test email',
        timestamp: new Date().toISOString(),
      });
    } finally {
      setTestEmailLoading(false);
    }
  };

  const handleTestLogger = async () => {
    setTestLoggerLoading(true);
    setTestLoggerResponse(null);
    try {
      const response = await apiClient.post('/api/admin/test-logger');
      setTestLoggerResponse({
        success: true,
        data: response.data,
        timestamp: new Date().toISOString(),
      });
    } catch (error: any) {
      setTestLoggerResponse({
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to send test TG Logger message',
        timestamp: new Date().toISOString(),
      });
    } finally {
      setTestLoggerLoading(false);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return (
      <div className="sadm-page" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', boxSizing: 'border-box' }}>
        <style>{`
          @media (max-width: 700px) {
            .sadm-page { padding: 12px 8px !important; }
          }
        `}</style>
        <h1>Services Administration</h1>
        <div style={{
          background: '#ffebee',
          border: '1px solid #f44336',
          padding: '15px',
          borderRadius: '4px',
          marginTop: '20px',
        }}>
          <strong>Error loading services:</strong>
          <pre style={{
            marginTop: '10px',
            padding: '10px',
            background: '#f5f5f5',
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '12px',
          }}>
            {JSON.stringify(error, null, 2)}
          </pre>
          <button
            onClick={() => refetch()}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sadm-page" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', boxSizing: 'border-box' }}>
      <style>{`
        @media (max-width: 700px) {
          .sadm-page { padding: 12px 8px !important; }
          .sadm-table-wrap { margin: 0 -8px; }
          .sadm-row { flex-wrap: wrap !important; gap: 8px !important; }
          .sadm-form input[type="text"],
          .sadm-form input[type="url"],
          .sadm-form input[type="number"],
          .sadm-form textarea,
          .sadm-form select {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          .sadm-modal-overlay { padding: 12px !important; align-items: flex-start !important; }
          .sadm-modal {
            width: min(600px, calc(100vw - 24px)) !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          .sadm-perm-grid { grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)) !important; }
        }
      `}</style>
      <h1>Services Administration</h1>
      <p style={{ marginTop: 8 }}>
        <Link to="/admin/scheduler">Scheduler tasks (cron)</Link>
        {' · '}
        <Link to="/admin">Admin hub</Link>
      </p>

      {newClientSecret && (
        <div
          style={{
            background: '#4caf50',
            color: 'white',
            padding: '15px',
            marginBottom: '20px',
            borderRadius: '4px',
          }}
        >
          <strong>New Client Secret:</strong> {newClientSecret}
          <br />
          <small>Save this now - it won't be shown again!</small>
        </div>
      )}

      <div className="sadm-row" style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button onClick={() => setShowCreateForm(!showCreateForm)}>
          {showCreateForm ? 'Cancel' : 'Create New Service'}
        </button>
        <button
          onClick={handleTestNotification}
          disabled={testNotificationLoading}
          style={{
            backgroundColor: testNotificationLoading ? '#ccc' : '#2196F3',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: testNotificationLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {testNotificationLoading ? 'Sending...' : '🧪 Test Notification'}
        </button>
        <button
          onClick={handleTestEmail}
          disabled={testEmailLoading}
          style={{
            backgroundColor: testEmailLoading ? '#ccc' : '#FF9800',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: testEmailLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {testEmailLoading ? 'Sending...' : '📧 Test Email'}
        </button>
        <button
          onClick={handleTestLogger}
          disabled={testLoggerLoading}
          style={{
            backgroundColor: testLoggerLoading ? '#ccc' : '#9C27B0',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: testLoggerLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {testLoggerLoading ? 'Sending...' : 'Test TG Logger'}
        </button>
      </div>

      {testNotificationResponse && (
        <div
          style={{
            background: testNotificationResponse.success ? '#e8f5e9' : '#ffebee',
            border: `1px solid ${testNotificationResponse.success ? '#4caf50' : '#f44336'}`,
            padding: '15px',
            marginBottom: '20px',
            borderRadius: '4px',
          }}
        >
          <strong>{testNotificationResponse.success ? '✅ Success' : '❌ Error'}</strong>
          <div style={{ marginTop: '8px', fontSize: '13px' }}>
            Sends a real messenger notification to your current admin account and should fan out to push when enabled.
          </div>
          <pre style={{
            marginTop: '10px',
            padding: '10px',
            background: '#f5f5f5',
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '12px',
          }}>
            {JSON.stringify(testNotificationResponse, null, 2)}
          </pre>
          <button
            onClick={() => setTestNotificationResponse(null)}
            style={{
              marginTop: '10px',
              padding: '4px 8px',
              fontSize: '12px',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {testEmailResponse && (
        <div
          style={{
            background: testEmailResponse.success ? '#e8f5e9' : '#ffebee',
            border: `1px solid ${testEmailResponse.success ? '#4caf50' : '#f44336'}`,
            padding: '15px',
            marginBottom: '20px',
            borderRadius: '4px',
          }}
        >
          <strong>{testEmailResponse.success ? '✅ Success' : '❌ Error'}</strong>
          <pre style={{
            marginTop: '10px',
            padding: '10px',
            background: '#f5f5f5',
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '12px',
          }}>
            {JSON.stringify(testEmailResponse, null, 2)}
          </pre>
          <button
            onClick={() => setTestEmailResponse(null)}
            style={{
              marginTop: '10px',
              padding: '4px 8px',
              fontSize: '12px',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {testLoggerResponse && (
        <div
          style={{
            background: testLoggerResponse.success ? '#e8f5e9' : '#ffebee',
            border: `1px solid ${testLoggerResponse.success ? '#4caf50' : '#f44336'}`,
            padding: '15px',
            marginBottom: '20px',
            borderRadius: '4px',
          }}
        >
          <strong>{testLoggerResponse.success ? '✅ Success' : '❌ Error'}</strong>
          <pre style={{
            marginTop: '10px',
            padding: '10px',
            background: '#f5f5f5',
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '12px',
          }}>
            {JSON.stringify(testLoggerResponse, null, 2)}
          </pre>
          <button
            onClick={() => setTestLoggerResponse(null)}
            style={{
              marginTop: '10px',
              padding: '4px 8px',
              fontSize: '12px',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {showCreateForm && (
        <form className="sadm-form" onSubmit={handleCreate} style={{ border: '1px solid #ccc', padding: '20px', marginBottom: '20px', boxSizing: 'border-box' }}>
          <h2>Create Service</h2>
          <div>
            <label>Name:</label>
            <input type="text" name="name" required style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label>Description:</label>
            <textarea name="description" style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label>Logo URL:</label>
            <input type="url" name="logoUrl" style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label>Client Type:</label>
            <select name="clientType" required style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
              <option value="confidential">Confidential</option>
              <option value="public">Public</option>
            </select>
          </div>
          <div>
            <label>Redirect URIs (comma-separated):</label>
            <input type="text" name="redirectUris" required style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label>Allowed Scopes (comma-separated):</label>
            <input type="text" name="allowedScopes" defaultValue="openid,profile,email" style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label>Grant Types (comma-separated):</label>
            <input type="text" name="grantTypes" defaultValue="authorization_code,refresh_token,client_credentials" style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label>
              <input type="checkbox" name="requirePkce" /> Require PKCE
            </label>
          </div>
          <div>
            <label>
              <input type="checkbox" name="trusted" /> Trusted (skip consent)
            </label>
          </div>
          <div>
            <label>
              <input type="checkbox" name="active" defaultChecked /> Active
            </label>
          </div>
          <div>
            <label>Webhook URL:</label>
            <input type="url" name="webhookUrl" style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label>Access Token Lifetime (seconds):</label>
            <input type="number" name="accessTokenLifetime" defaultValue="3600" style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label>Refresh Token Lifetime (seconds):</label>
            <input type="number" name="refreshTokenLifetime" defaultValue="604800" style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
          </div>
          <h3>Permissions</h3>
          <div className="sadm-perm-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
          {Object.keys(defaultPermissions).map((key) => (
            <div key={key}>
              <label>
                <input type="checkbox" name={`perm.${key}`} /> {key}
              </label>
            </div>
          ))}
          </div>
          <button type="submit">Create</button>
        </form>
      )}

      <div className="sadm-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Client ID</th>
            <th>Type</th>
            <th>Active</th>
            <th>Grant Types</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {services.map((service: Service) => (
            <tr key={service.clientId}>
              <td>{service.name}</td>
              <td>
                <code>{service.clientId}</code>
              </td>
              <td>{service.clientType}</td>
              <td>{service.active ? 'Yes' : 'No'}</td>
              <td>{service.grantTypes.join(', ')}</td>
              <td>
                <button onClick={() => setEditingService(service)}>Edit</button>
                <button onClick={() => handleDelete(service.clientId)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {editingService && (
        <div className="sadm-modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px', boxSizing: 'border-box' }}>
          <div className="sadm-modal" style={{ background: 'white', padding: '20px', width: 'min(600px, calc(100vw - 24px))', maxWidth: '100%', maxHeight: '80vh', overflow: 'auto', boxSizing: 'border-box' }}>
            <h2>Edit Service: {editingService.name}</h2>
            <form className="sadm-form" onSubmit={handleUpdate}>
              <div>
                <label>Name:</label>
                <input type="text" name="name" defaultValue={editingService.name} required style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label>Description:</label>
                <textarea name="description" defaultValue={editingService.description} style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label>Logo URL:</label>
                <input type="url" name="logoUrl" defaultValue={editingService.logoUrl} style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label>Redirect URIs (comma-separated):</label>
                <input type="text" name="redirectUris" defaultValue={editingService.redirectUris.join(', ')} required style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label>Allowed Scopes (comma-separated):</label>
                <input type="text" name="allowedScopes" defaultValue={editingService.allowedScopes.join(', ')} style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label>Grant Types (comma-separated):</label>
                <input type="text" name="grantTypes" defaultValue={editingService.grantTypes.join(', ')} style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label>
                  <input type="checkbox" name="requirePkce" defaultChecked={editingService.requirePkce} /> Require PKCE
                </label>
              </div>
              <div>
                <label>
                  <input type="checkbox" name="trusted" defaultChecked={editingService.trusted} /> Trusted
                </label>
              </div>
              <div>
                <label>
                  <input type="checkbox" name="active" defaultChecked={editingService.active} /> Active
                </label>
              </div>
              <div>
                <label>Webhook URL:</label>
                <input type="url" name="webhookUrl" defaultValue={editingService.webhookUrl} style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label>Access Token Lifetime (seconds):</label>
                <input type="number" name="accessTokenLifetime" defaultValue={editingService.accessTokenLifetime} style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label>Refresh Token Lifetime (seconds):</label>
                <input type="number" name="refreshTokenLifetime" defaultValue={editingService.refreshTokenLifetime} style={{ maxWidth: '100%', boxSizing: 'border-box' }} />
              </div>
              <h3>Permissions</h3>
              <div className="sadm-perm-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
              {Object.keys(defaultPermissions).map((key) => (
                <div key={key}>
                  <label>
                    <input type="checkbox" name={`perm.${key}`} defaultChecked={editingService.permissions?.[key as keyof ServicePermissions]} /> {key}
                  </label>
                </div>
              ))}
              </div>
              <div className="sadm-row" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                <button type="submit">Update</button>
                <button type="button" onClick={() => setEditingService(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesAdmin;
